/**
 * Lecture Player Page
 * Split view: Video player on left, AI chat on right
 * Works for both students and teachers
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  getLecture,
  LectureResponse,
  submitReflection,
  getMyReflection,
  ReflectionResponse,
  generateLectureNotes,
  getLectureNotes,
  LectureNotesResponse,
  getReflectionAnalytics,
  ReflectionAnalyticsResponse,
  saveWatchProgress,
  getMyWatchProgress,
} from '../../api/courses';
import VideoPlayer, { VideoPlayerHandle } from '../../components/player/VideoPlayer';
import ChatPanel from '../../components/player/ChatPanel';
import Mascot from '../../components/shared/Mascot';
import { getStaticBaseUrl } from '../../api/client';

const LecturePlayerPage: React.FC = () => {
  const { courseId, lectureId } = useParams<{ courseId: string; lectureId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Detect if teacher or student based on URL path
  const isTeacher = location.pathname.includes('/teacher/');

  const [lecture, setLecture] = useState<LectureResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const videoPlayerRef = useRef<VideoPlayerHandle>(null);

  // Continue-watching: track playback position, resume where the user left off
  const watchPosRef = useRef(0);
  const watchDurRef = useRef(0);
  const lastSavedPosRef = useRef(0);
  const resumeTargetRef = useRef<number | null>(null);
  const resumeDoneRef = useRef(false);

  // Reflection state (students only)
  const [interestingTopics, setInterestingTopics] = useState<string[]>([]);
  const [challengingTopics, setChallengingTopics] = useState<string[]>([]);
  const [interestingComment, setInterestingComment] = useState('');
  const [challengingComment, setChallengingComment] = useState('');
  const [reflectionSubmitting, setReflectionSubmitting] = useState(false);
  const [reflectionSaved, setReflectionSaved] = useState(false);
  const [existingReflection, setExistingReflection] = useState<ReflectionResponse | null>(null);

  // Lecture Notes state
  const [lectureNotes, setLectureNotes] = useState<LectureNotesResponse | null>(null);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesGenerating, setNotesGenerating] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [studentNotesDownloading, setStudentNotesDownloading] = useState(false);

  // Reflection Analytics state (teachers only)
  const [reflectionAnalytics, setReflectionAnalytics] = useState<ReflectionAnalyticsResponse | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsExpanded, setAnalyticsExpanded] = useState(false);

  // Load lecture
  useEffect(() => {
    if (lectureId) {
      loadLecture();
    }
  }, [lectureId]);

  // Fetch saved watch position; resume if meaningfully into the video
  useEffect(() => {
    if (!lectureId) return;
    resumeDoneRef.current = false;
    resumeTargetRef.current = null;
    lastSavedPosRef.current = 0;
    getMyWatchProgress(lectureId)
      .then((p) => {
        if (p && p.position_seconds > 30 && (p.duration_seconds === 0 || p.percent < 95)) {
          resumeTargetRef.current = p.position_seconds;
        }
      })
      .catch(() => {});
  }, [lectureId]);

  // Save the latest position when the user leaves the page
  useEffect(() => {
    return () => {
      if (lectureId && watchPosRef.current > 5) {
        saveWatchProgress(lectureId, watchPosRef.current, watchDurRef.current).catch(() => {});
      }
    };
  }, [lectureId]);

  const handleWatchTimeUpdate = (time: number) => {
    // Jump to the saved position once playback actually starts
    if (resumeTargetRef.current !== null && !resumeDoneRef.current) {
      resumeDoneRef.current = true;
      const target = resumeTargetRef.current;
      resumeTargetRef.current = null;
      if (time < target - 5) {
        videoPlayerRef.current?.seekTo(target);
        return;
      }
    }
    watchPosRef.current = time;
    if (lecture?.duration) watchDurRef.current = lecture.duration;
    // Throttled save: every 10 seconds of playback (or after a backward seek)
    if (lectureId && (time - lastSavedPosRef.current > 10 || time < lastSavedPosRef.current - 10)) {
      lastSavedPosRef.current = time;
      saveWatchProgress(lectureId, time, watchDurRef.current).catch(() => {});
    }
  };

  // Load existing reflection (students only)
  useEffect(() => {
    if (lectureId && !isTeacher) {
      getMyReflection(lectureId).then((r) => {
        if (r) {
          setExistingReflection(r);
          setInterestingTopics(r.interesting_topics);
          setChallengingTopics(r.challenging_topics);
          setInterestingComment(r.interesting_comment || '');
          setChallengingComment(r.challenging_comment || '');
          setReflectionSaved(true);
        }
      }).catch(() => {});
    }
  }, [lectureId, isTeacher]);

  const loadLecture = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getLecture(lectureId!);
      setLecture(data);
    } catch (err: any) {
      setError('Failed to load lecture');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Load lecture notes (both teachers and students)
  useEffect(() => {
    if (lectureId) {
      getLectureNotes(lectureId).then((notes) => {
        if (notes) setLectureNotes(notes);
      }).catch(() => {});
    }
  }, [lectureId]);

  // Load teacher-only data (reflection analytics)
  useEffect(() => {
    if (lectureId && isTeacher) {
      getReflectionAnalytics(lectureId).then((analytics) => {
        setReflectionAnalytics(analytics);
      }).catch(() => {});
    }
  }, [lectureId, isTeacher]);

  const handleGenerateNotes = async () => {
    if (!lectureId) return;
    try {
      setNotesGenerating(true);
      await generateLectureNotes(lectureId);
      // Poll for completion
      const poll = setInterval(async () => {
        const notes = await getLectureNotes(lectureId);
        if (notes) {
          clearInterval(poll);
          setNotesGenerating(false);
          setLectureNotes(notes);
          setNotesExpanded(true);
        }
      }, 3000);
      setTimeout(() => { clearInterval(poll); setNotesGenerating(false); }, 180000);
    } catch (err: any) {
      setNotesGenerating(false);
      alert(err.response?.data?.detail || 'Failed to generate notes');
    }
  };

  const handleViewNotes = async () => {
    if (!lectureId) return;
    if (lectureNotes) {
      setNotesExpanded(!notesExpanded);
      return;
    }
    try {
      setNotesLoading(true);
      const notes = await getLectureNotes(lectureId);
      if (notes) {
        setLectureNotes(notes);
        setNotesExpanded(true);
      } else {
        handleGenerateNotes();
      }
    } catch (err) {
      console.error('Failed to load notes:', err);
    } finally {
      setNotesLoading(false);
    }
  };

  const renderNotesMarkdown = (text: string) => {
    return text
      .replace(/^### (.*$)/gm, '<h4 class="text-base font-bold text-primary-700 dark:text-primary-300 mt-5 mb-2 flex items-center gap-2"><span class="w-1.5 h-1.5 bg-primary-500 rounded-full inline-block"></span>$1</h4>')
      .replace(/^## Key Takeaways$/gm, '<h3 class="text-lg font-bold text-emerald-600 dark:text-emerald-400 mt-8 mb-3 pb-2 border-b-2 border-emerald-200 dark:border-emerald-800">Key Takeaways</h3>')
      .replace(/^## Glossary$/gm, '<h3 class="text-lg font-bold text-amber-600 dark:text-amber-400 mt-8 mb-3 pb-2 border-b-2 border-amber-200 dark:border-amber-800">Glossary</h3>')
      .replace(/^## (.*$)/gm, '<h3 class="text-lg font-bold text-primary-700 dark:text-primary-300 mt-8 mb-3 pb-2 border-b-2 border-primary-200 dark:border-primary-800">$1</h3>')
      .replace(/^# (.*$)/gm, '<h2 class="text-2xl font-extrabold tracking-tight text-gray-900 dark:text-white mt-4 mb-4 pb-3 border-b-2 border-primary-200 dark:border-primary-700">$1</h2>')
      .replace(/^> \*\*(.*?)\*\*:?\s*(.*$)/gm, '<div class="my-3 p-4 bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-500 rounded-r-2xl"><span class="font-bold text-primary-700 dark:text-primary-300">$1</span> <span class="text-gray-600 dark:text-gray-300">$2</span></div>')
      .replace(/^> (.*$)/gm, '<div class="my-3 p-4 bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 rounded-r-2xl text-gray-600 dark:text-gray-300 italic">$1</div>')
      .replace(/^---$/gm, '<hr class="my-6 border-t-2 border-gray-200 dark:border-gray-700"/>')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-900 dark:text-white font-semibold">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/^(\d+)\. (.*$)/gm, '<div class="flex gap-3 my-1.5 ml-2"><span class="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full flex items-center justify-center text-xs font-bold">$1</span><span class="text-gray-600 dark:text-gray-300">$2</span></div>')
      .replace(/^- (.*$)/gm, '<div class="flex gap-2 my-1 ml-4"><span class="flex-shrink-0 w-1.5 h-1.5 bg-primary-500 rounded-full mt-2"></span><span class="text-gray-600 dark:text-gray-300">$1</span></div>')
      .replace(/\n\n/g, '<div class="h-3"></div>')
      .replace(/\n/g, '<br/>');
  };

  // Handle timestamp click from chat
  const handleTimestampClick = (time: number) => {
    videoPlayerRef.current?.seekTo(time);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-secondary-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="text-sm font-bold text-gray-500 dark:text-gray-400">Loading lecture...</p>
        </div>
      </div>
    );
  }

  if (error || !lecture) {
    const backPath = isTeacher ? `/teacher/courses/${courseId}` : `/student/courses/${courseId}`;
    return (
      <div className="min-h-screen bg-white dark:bg-secondary-900 flex items-center justify-center px-6">
        <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 px-8 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
          <Mascot name="folder" chip="#93c5fd" className="h-20 w-20 shadow-medium animate-float-slow" blinkDelay={0.4} />
          <h3 className="mt-5 text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">{error || 'Lecture not found'}</h3>
          <div className="mt-6">
            <button
              onClick={() => navigate(backPath)}
              className="rounded-full bg-primary-600 px-6 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
            >
              Back to Course
            </button>
          </div>
        </div>
      </div>
    );
  }

  const apiUrl = getStaticBaseUrl();
  const videoUrl = `${apiUrl}${lecture.video_path}`;
  const backPath = isTeacher ? `/teacher/courses/${courseId}` : `/student/courses/${courseId}`;

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-white dark:bg-secondary-900">
      {/* Header */}
      <div className="flex-shrink-0 bg-white/80 dark:bg-secondary-900/80 backdrop-blur-md border-b border-gray-200 dark:border-secondary-700">
        <div className="max-w-screen-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={() => navigate(backPath)}
              className="inline-flex items-center gap-2 rounded-full border-2 border-gray-200 px-4 py-2 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-50 dark:border-secondary-700 dark:text-gray-200 dark:hover:bg-secondary-800"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Course
            </button>
            <h1 className="flex-1 text-center text-xl font-extrabold tracking-tight text-gray-900 dark:text-white truncate">{lecture.title}</h1>
            <div className="w-32 shrink-0"></div>
          </div>
        </div>
      </div>

      {/* Main Content - Split View */}
      <div className="flex-1 min-h-0 max-w-screen-2xl mx-auto w-full">
        <div className="grid grid-cols-1 lg:grid-cols-3 h-full relative">
          {/* Left: Video Player (2/3 width) */}
          <div className="lg:col-span-2 flex flex-col min-h-0 overflow-y-auto p-4 pr-2">
            {/* Video Container */}
            <div className="bg-black rounded-3xl overflow-hidden flex-shrink-0 shadow-large ring-1 ring-gray-200 dark:ring-secondary-700">
              <VideoPlayer
                ref={videoPlayerRef}
                videoUrl={videoUrl}
                lectureTitle={lecture.title}
                onTimeUpdate={handleWatchTimeUpdate}
                onSeek={(time) => {}}
              />
            </div>

            {/* Info Card */}
            <div className="mt-4 flex-shrink-0 rounded-2xl border border-gray-200 bg-white p-5 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <span
                    className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </span>
                  <h2 className="text-lg font-extrabold text-gray-900 dark:text-white">{lecture.title}</h2>
                </div>
                {!isTeacher && (
                  <button
                    onClick={async () => {
                      if (!lectureId) return;
                      if (lectureNotes) {
                        const w = window.open('', '_blank');
                        if (w) {
                          w.document.write(`
                            <html><head><title>${lectureNotes.lecture_title} - Lecture Notes</title>
                            <style>
                              body { font-family: 'Georgia', serif; max-width: 800px; margin: 40px auto; padding: 0 20px; color: #333; line-height: 1.8; }
                              h1 { color: #2c4954; border-bottom: 3px solid #5891a7; padding-bottom: 10px; }
                              h2 { color: #2c4954; border-bottom: 2px solid #b3d0da; padding-bottom: 6px; margin-top: 30px; }
                              h3 { color: #3f6f81; margin-top: 25px; }
                              h4 { color: #5891a7; margin-top: 20px; }
                              blockquote { background: #eef5f7; border-left: 4px solid #5891a7; padding: 12px 16px; margin: 15px 0; border-radius: 0 8px 8px 0; }
                              strong { color: #2c4954; }
                              li { margin: 4px 0; }
                              hr { border: none; border-top: 2px solid #e5e7eb; margin: 25px 0; }
                              @media print { body { margin: 20px; } }
                            </style></head><body>
                            <h1>${lectureNotes.lecture_title}</h1>
                            ${lectureNotes.content.replace(/\n/g, '<br/>')}
                            </body></html>
                          `);
                          w.document.close();
                        }
                        return;
                      }
                      try {
                        setStudentNotesDownloading(true);
                        const notes = await getLectureNotes(lectureId);
                        if (notes) {
                          setLectureNotes(notes);
                          const w = window.open('', '_blank');
                          if (w) {
                            w.document.write(`
                              <html><head><title>${notes.lecture_title} - Lecture Notes</title>
                              <style>
                                body { font-family: 'Georgia', serif; max-width: 800px; margin: 40px auto; padding: 0 20px; color: #333; line-height: 1.8; }
                                h1 { color: #2c4954; border-bottom: 3px solid #5891a7; padding-bottom: 10px; }
                                h2 { color: #2c4954; border-bottom: 2px solid #b3d0da; padding-bottom: 6px; margin-top: 30px; }
                                h3 { color: #3f6f81; margin-top: 25px; }
                                h4 { color: #5891a7; margin-top: 20px; }
                                blockquote { background: #eef5f7; border-left: 4px solid #5891a7; padding: 12px 16px; margin: 15px 0; border-radius: 0 8px 8px 0; }
                                strong { color: #2c4954; }
                                li { margin: 4px 0; }
                                hr { border: none; border-top: 2px solid #e5e7eb; margin: 25px 0; }
                                @media print { body { margin: 20px; } }
                              </style></head><body>
                              <h1>${notes.lecture_title}</h1>
                              ${notes.content.replace(/\n/g, '<br/>')}
                              </body></html>
                            `);
                            w.document.close();
                          }
                        } else {
                          alert('Lecture notes are not available yet. The instructor needs to generate them first.');
                        }
                      } catch (err) {
                        alert('Lecture notes are not available yet.');
                      } finally {
                        setStudentNotesDownloading(false);
                      }
                    }}
                    disabled={studentNotesDownloading}
                    className="flex-shrink-0 flex items-center gap-1.5 rounded-full bg-primary-600 px-4 py-1.5 text-xs font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none disabled:opacity-50"
                  >
                    {studentNotesDownloading ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-900"></div>
                        Loading...
                      </>
                    ) : (
                      <>
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Lecture Notes
                      </>
                    )}
                  </button>
                )}
              </div>
              {lecture.description && (
                <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">{lecture.description}</p>
              )}
              <div className="mt-3 flex items-center gap-4 text-xs font-medium text-gray-500 dark:text-gray-400">
                <span className="inline-flex items-center gap-1.5">
                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {Math.floor(lecture.duration / 60)}m {Math.floor(lecture.duration % 60)}s
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Uploaded {new Date(lecture.uploaded_at).toLocaleDateString()}
                </span>
              </div>

              {/* Topics */}
              {lecture.topics && lecture.topics.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-secondary-700">
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Topics covered</p>
                  <div className="flex flex-wrap gap-1.5">
                    {lecture.topics.map((topic: string, idx: number) => (
                      <span
                        key={idx}
                        className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600 dark:bg-secondary-700 dark:text-gray-300"
                      >
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Teacher: Lecture Notes */}
            {isTeacher && (
              <div className="mt-4 flex-shrink-0 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                <button
                  onClick={handleViewNotes}
                  disabled={notesGenerating || notesLoading}
                  className="w-full flex items-center justify-between p-5 hover:bg-gray-50 dark:hover:bg-secondary-700/40 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300"
                    >
                      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </span>
                    <div className="text-left">
                      <h3 className="text-sm font-extrabold text-gray-900 dark:text-white">Lecture Notes</h3>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        {notesGenerating ? 'Generating notes...' : notesLoading ? 'Loading...' : lectureNotes ? `Generated ${new Date(lectureNotes.generated_at).toLocaleDateString()}` : 'Click to generate AI notes'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {(notesGenerating || notesLoading) && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
                    )}
                    <svg className={`w-5 h-5 text-gray-400 transition-transform ${notesExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
                {notesExpanded && lectureNotes && (
                  <div className="border-t border-gray-100 dark:border-secondary-700">
                    <div className="p-5 max-h-[50vh] overflow-y-auto">
                      <div
                        className="notes-content text-sm leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: renderNotesMarkdown(lectureNotes.content) }}
                      />
                    </div>
                    <div className="px-5 py-3 border-t border-gray-100 dark:border-secondary-700 flex items-center justify-between bg-gray-50 dark:bg-secondary-800/50">
                      <button
                        onClick={handleGenerateNotes}
                        disabled={notesGenerating}
                        className="flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5 text-xs font-bold text-gray-600 transition-colors hover:bg-gray-200 disabled:opacity-50 dark:bg-secondary-700 dark:text-gray-300 dark:hover:bg-secondary-600"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Regenerate
                      </button>
                      <button
                        onClick={() => {
                          const w = window.open('', '_blank');
                          if (w) {
                            w.document.write(`
                              <html><head><title>${lectureNotes.lecture_title} - Notes</title>
                              <style>
                                body { font-family: 'Georgia', serif; max-width: 800px; margin: 40px auto; padding: 0 20px; color: #333; line-height: 1.8; }
                                h1 { color: #2c4954; border-bottom: 3px solid #5891a7; padding-bottom: 10px; }
                                h2 { color: #2c4954; border-bottom: 2px solid #b3d0da; padding-bottom: 6px; margin-top: 30px; }
                                h3 { color: #3f6f81; margin-top: 25px; }
                                h4 { color: #5891a7; margin-top: 20px; }
                                blockquote { background: #eef5f7; border-left: 4px solid #5891a7; padding: 12px 16px; margin: 15px 0; border-radius: 0 8px 8px 0; }
                                strong { color: #2c4954; }
                                li { margin: 4px 0; }
                                hr { border: none; border-top: 2px solid #e5e7eb; margin: 25px 0; }
                                @media print { body { margin: 20px; } }
                              </style></head><body>
                              ${lectureNotes.content.replace(/\n/g, '<br/>')}
                              </body></html>
                            `);
                            w.document.close();
                            w.print();
                          }
                        }}
                        className="flex items-center gap-2 rounded-full bg-primary-600 px-4 py-1.5 text-xs font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                        Print
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Teacher: Reflection Analytics */}
            {isTeacher && lecture.topics && lecture.topics.length > 0 && (
              <div className="mt-4 flex-shrink-0 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                <button
                  onClick={() => setAnalyticsExpanded(!analyticsExpanded)}
                  className="w-full flex items-center justify-between p-5 hover:bg-gray-50 dark:hover:bg-secondary-700/40 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300"
                    >
                      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </span>
                    <div className="text-left">
                      <h3 className="text-sm font-extrabold text-gray-900 dark:text-white">Student Reflections</h3>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        {reflectionAnalytics
                          ? `${reflectionAnalytics.total_responses} student${reflectionAnalytics.total_responses !== 1 ? 's' : ''} responded`
                          : 'No reflections yet'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {reflectionAnalytics && reflectionAnalytics.total_responses > 0 && (
                      <span
                        className="rounded-full bg-primary-100 px-3 py-1 text-xs font-bold text-primary-800 dark:bg-primary-900/30 dark:text-primary-300"
                      >
                        {reflectionAnalytics.total_responses}
                      </span>
                    )}
                    <svg className={`w-5 h-5 text-gray-400 transition-transform ${analyticsExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
                {analyticsExpanded && (
                  <div className="border-t border-gray-100 dark:border-secondary-700 p-5">
                    {!reflectionAnalytics || reflectionAnalytics.total_responses === 0 ? (
                      <div className="text-center py-6">
                        <svg className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                        <p className="mt-2 text-sm font-bold text-gray-500 dark:text-gray-400">No student reflections yet</p>
                        <p className="mt-1 text-xs font-medium text-gray-500 dark:text-gray-400">Students can submit reflections after watching</p>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {/* Most Interesting Topics */}
                        <div>
                          <h4 className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                            </svg>
                            Most Interesting
                          </h4>
                          {Object.keys(reflectionAnalytics.interesting_counts).length === 0 ? (
                            <p className="text-xs text-gray-500 dark:text-gray-400 italic">No topics selected yet</p>
                          ) : (
                            <div className="space-y-2">
                              {Object.entries(reflectionAnalytics.interesting_counts)
                                .sort(([, a], [, b]) => b - a)
                                .map(([topic, count]) => {
                                  const percentage = Math.round((count / reflectionAnalytics.total_responses) * 100);
                                  return (
                                    <div key={`int-${topic}`}>
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{topic}</span>
                                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                          {count}/{reflectionAnalytics.total_responses} ({percentage}%)
                                        </span>
                                      </div>
                                      <div className="w-full bg-gray-100 dark:bg-secondary-700 rounded-full h-2">
                                        <div
                                          className="bg-gradient-to-r from-emerald-500 to-teal-500 h-2 rounded-full transition-all duration-500"
                                          style={{ width: `${percentage}%` }}
                                        ></div>
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          )}
                        </div>

                        {/* Most Challenging Topics */}
                        <div>
                          <h4 className="text-xs font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            Most Challenging
                          </h4>
                          {Object.keys(reflectionAnalytics.challenging_counts).length === 0 ? (
                            <p className="text-xs text-gray-500 dark:text-gray-400 italic">No topics selected yet</p>
                          ) : (
                            <div className="space-y-2">
                              {Object.entries(reflectionAnalytics.challenging_counts)
                                .sort(([, a], [, b]) => b - a)
                                .map(([topic, count]) => {
                                  const percentage = Math.round((count / reflectionAnalytics.total_responses) * 100);
                                  return (
                                    <div key={`ch-${topic}`}>
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{topic}</span>
                                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                          {count}/{reflectionAnalytics.total_responses} ({percentage}%)
                                        </span>
                                      </div>
                                      <div className="w-full bg-gray-100 dark:bg-secondary-700 rounded-full h-2">
                                        <div
                                          className="bg-gradient-to-r from-amber-500 to-orange-500 h-2 rounded-full transition-all duration-500"
                                          style={{ width: `${percentage}%` }}
                                        ></div>
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Free-text reflection comments */}
                    {((reflectionAnalytics?.comments) || []).length > 0 && (
                      <div className="mt-5">
                        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          What students wrote
                        </p>
                        <div className="space-y-2">
                          {((reflectionAnalytics?.comments) || []).map((c, i) => (
                            <div
                              key={i}
                              className="rounded-xl border border-gray-100 bg-gray-50 p-3 dark:border-secondary-700 dark:bg-secondary-700/40"
                            >
                              <p className="text-xs font-bold text-gray-500 dark:text-gray-400">{c.student_username}</p>
                              {c.interesting_comment && (
                                <p className="mt-1 text-xs leading-relaxed text-gray-700 dark:text-gray-300">
                                  <span className="font-bold text-emerald-600 dark:text-emerald-400">Interesting: </span>
                                  {c.interesting_comment}
                                </p>
                              )}
                              {c.challenging_comment && (
                                <p className="mt-1 text-xs leading-relaxed text-gray-700 dark:text-gray-300">
                                  <span className="font-bold text-amber-600 dark:text-amber-400">Challenging: </span>
                                  {c.challenging_comment}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Reflection Form (students only) */}
            {!isTeacher && lecture.topics && lecture.topics.length > 0 && (
              <div className="mt-4 flex-shrink-0 rounded-2xl border border-gray-200 bg-white p-5 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span
                      className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300"
                    >
                      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </span>
                    <h3 className="text-sm font-extrabold text-gray-900 dark:text-white">Lecture Reflection</h3>
                  </div>
                  {reflectionSaved && (
                    <span className="text-xs font-bold text-emerald-600 dark:text-green-400 flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Saved
                    </span>
                  )}
                </div>

                {/* Interesting Topics */}
                <div className="mb-4">
                  <p className="text-xs font-bold text-gray-600 dark:text-gray-300 mb-2">Which topics did you find most interesting?</p>
                  <div className="flex flex-wrap gap-2">
                    {lecture.topics.map((topic: string) => {
                      const selected = interestingTopics.includes(topic);
                      return (
                        <button
                          key={`int-${topic}`}
                          onClick={() => {
                            setInterestingTopics(prev => selected ? prev.filter(t => t !== topic) : [...prev, topic]);
                            setReflectionSaved(false);
                          }}
                          className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${
                            selected
                              ? 'bg-emerald-50 text-emerald-700 ring-2 ring-emerald-400 dark:bg-emerald-600/30 dark:text-emerald-300 dark:ring-emerald-500/50'
                              : 'bg-gray-100 text-gray-500 ring-1 ring-gray-200 hover:ring-gray-300 hover:text-gray-700 dark:bg-secondary-700/50 dark:text-gray-400 dark:ring-secondary-600/50 dark:hover:ring-secondary-500/50 dark:hover:text-gray-300'
                          }`}
                        >
                          {selected && <span className="mr-1">+</span>}
                          {topic}
                        </button>
                      );
                    })}
                  </div>
                  <textarea
                    rows={2}
                    value={interestingComment}
                    onChange={(e) => { setInterestingComment(e.target.value); setReflectionSaved(false); }}
                    placeholder="Tell us more: what made it interesting? (optional)"
                    className="mt-2 block w-full rounded-xl border-2 border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-secondary-600 dark:bg-secondary-700 dark:text-white dark:placeholder-secondary-400"
                  />
                </div>

                {/* Challenging Topics */}
                <div className="mb-4">
                  <p className="text-xs font-bold text-gray-600 dark:text-gray-300 mb-2">Which topics did you find most challenging?</p>
                  <div className="flex flex-wrap gap-2">
                    {lecture.topics.map((topic: string) => {
                      const selected = challengingTopics.includes(topic);
                      return (
                        <button
                          key={`ch-${topic}`}
                          onClick={() => {
                            setChallengingTopics(prev => selected ? prev.filter(t => t !== topic) : [...prev, topic]);
                            setReflectionSaved(false);
                          }}
                          className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${
                            selected
                              ? 'bg-amber-50 text-amber-700 ring-2 ring-amber-400 dark:bg-amber-600/30 dark:text-amber-300 dark:ring-amber-500/50'
                              : 'bg-gray-100 text-gray-500 ring-1 ring-gray-200 hover:ring-gray-300 hover:text-gray-700 dark:bg-secondary-700/50 dark:text-gray-400 dark:ring-secondary-600/50 dark:hover:ring-secondary-500/50 dark:hover:text-gray-300'
                          }`}
                        >
                          {selected && <span className="mr-1">+</span>}
                          {topic}
                        </button>
                      );
                    })}
                  </div>
                  <textarea
                    rows={2}
                    value={challengingComment}
                    onChange={(e) => { setChallengingComment(e.target.value); setReflectionSaved(false); }}
                    placeholder="Tell us more: what was confusing or hard? (optional)"
                    className="mt-2 block w-full rounded-xl border-2 border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-secondary-600 dark:bg-secondary-700 dark:text-white dark:placeholder-secondary-400"
                  />
                </div>

                {/* Submit */}
                <button
                  onClick={async () => {
                    if (interestingTopics.length === 0 && challengingTopics.length === 0 && !interestingComment.trim() && !challengingComment.trim()) return;
                    setReflectionSubmitting(true);
                    try {
                      await submitReflection(lectureId!, interestingTopics, challengingTopics, interestingComment.trim(), challengingComment.trim());
                      setReflectionSaved(true);
                    } catch (err) {
                      console.error('Failed to submit reflection:', err);
                    } finally {
                      setReflectionSubmitting(false);
                    }
                  }}
                  disabled={reflectionSubmitting || (interestingTopics.length === 0 && challengingTopics.length === 0 && !interestingComment.trim() && !challengingComment.trim()) || reflectionSaved}
                  className="mt-1 w-full rounded-full bg-primary-600 px-4 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {reflectionSubmitting ? 'Submitting...' : reflectionSaved ? 'Reflection Saved' : 'Submit Reflection'}
                </button>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="hidden lg:block absolute left-[66.666%] top-4 bottom-4 w-px">
            <div className="h-full w-full bg-gradient-to-b from-transparent via-gray-200 to-transparent dark:via-secondary-700"></div>
          </div>

          {/* Right: AI Chat Panel (1/3 width) */}
          <div className="lg:col-span-1 min-h-0 p-4 pl-2">
            <ChatPanel
              lectureName={lecture.title.replace(/ /g, '_').replace(/\//g, '_')}
              courseId={courseId!}
              onTimestampClick={handleTimestampClick}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LecturePlayerPage;
